import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { apiFetch } from "@/lib/api";

const TimerContext = createContext(null);
export const useTimer = () => useContext(TimerContext);

// Server-backed running timer. Only ONE active timer per user (enforced by the
// backend). Display ticks locally every second, but authoritative elapsed time
// is always derived from the server's timestamps (re-synced on foreground,
// network restore, and after every action; light 30s safety poll).
export function TimerProvider({ children }) {
  const [timer, setTimer] = useState({ active: false });
  const [display, setDisplay] = useState(0);
  const baseRef = useRef({ elapsed: 0, at: 0, running: false });

  const apply = useCallback((state) => {
    setTimer(state || { active: false });
    if (state?.active) {
      baseRef.current = {
        elapsed: state.elapsed_seconds || 0,
        at: Date.now(),
        running: state.state === "running",
      };
      setDisplay(state.elapsed_seconds || 0);
    } else {
      baseRef.current = { elapsed: 0, at: 0, running: false };
      setDisplay(0);
    }
  }, []);

  const sync = useCallback(async () => {
    try {
      const s = await apiFetch("/time/timer");
      apply(s);
      return s;
    } catch {
      return null;
    }
  }, [apply]);

  useEffect(() => { sync(); }, [sync]);

  // local tick (authoritative base from server; local clock only advances it)
  useEffect(() => {
    const id = setInterval(() => {
      const b = baseRef.current;
      if (b.running) {
        setDisplay(b.elapsed + Math.floor((Date.now() - b.at) / 1000));
      }
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // re-sync on foreground + network restore + light safety poll
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === "visible") sync(); };
    const onOnline = () => sync();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("online", onOnline);
    window.addEventListener("focus", onVis);
    const poll = setInterval(() => {
      if (document.visibilityState === "visible") sync();
    }, 30000);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("focus", onVis);
      clearInterval(poll);
    };
  }, [sync]);

  const start = useCallback(async (taskId, { activityType = "", note = "", force = false } = {}) => {
    // optimistic paint so the shell indicator appears immediately
    setTimer((prev) => (prev.active && prev.task_id === taskId ? prev
      : { active: true, state: "running", task_id: taskId, task_title: "", elapsed_seconds: 0, server_now: null }));
    baseRef.current = { elapsed: 0, at: Date.now(), running: true };
    try {
      const r = await apiFetch("/time/timer/start", {
        method: "POST",
        body: JSON.stringify({ task_id: taskId, activity_type: activityType, note, force }),
      });
      apply(r);
      return { ok: true };
    } catch (e) {
      // 409 conflict → an existing timer is already running for this user
      const cur = await apiFetch("/time/timer").catch(() => null);
      if (cur && cur.active) {
        apply(cur);
        return { ok: false, conflict: true, running_task_title: cur.task_title, running_task_id: cur.task_id };
      }
      apply({ active: false });   // roll back optimistic paint on real failure
      throw e;
    }
  }, [apply]);

  const pause = useCallback(async () => { apply(await apiFetch("/time/timer/pause", { method: "POST", body: "{}" })); }, [apply]);
  const resume = useCallback(async () => { apply(await apiFetch("/time/timer/resume", { method: "POST", body: "{}" })); }, [apply]);
  const stop = useCallback(async () => {
    const r = await apiFetch("/time/timer/stop", { method: "POST", body: "{}" });
    apply({ active: false });
    return r;
  }, [apply]);
  const discard = useCallback(async () => {
    await apiFetch("/time/timer/discard", { method: "POST", body: "{}" });
    apply({ active: false });
  }, [apply]);

  const value = { timer, display, sync, start, pause, resume, stop, discard,
    isRunningTask: (taskId) => timer.active && timer.task_id === taskId };
  return <TimerContext.Provider value={value}>{children}</TimerContext.Provider>;
}
